import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { BrgButton, ButtonVariant, CommonInput, INPUT_TYPES } from '@brg/ui'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { modalControllerActions } from 'core'
import SearchIcon16 from 'shared/icons/SearchIcon16'
import {
  TEST_DRAFT_COURSE_CATALOG,
  findTestDraft,
  getAllTestsFlat,
  type FlatTestRow
} from './mockTestDraftData'
import {
  ClockStatIcon,
  GradCapStatIcon,
  QuestionStatIcon
} from './TestPreviewIcons'
import {
  Root,
  MainTitle,
  IntroText,
  SearchRow,
  SearchIconWrap,
  SearchInput,
  SectionTitle,
  DropdownsGrid,
  DropdownField,
  PreviewCardsGrid,
  TestPreviewCard,
  PreviewBreadcrumbs,
  PreviewCardTitle,
  PreviewCardDescription,
  PreviewStatsRow,
  PreviewStatItem,
  CopySection,
  CopyCheckboxStack,
  CopyCheckboxRow,
  CopyActionSlot
} from './styles'

type DropdownOption = { label: string; value: string }

const placeholderFirst = (
  placeholder: string,
  rest: DropdownOption[]
): DropdownOption[] => [{ label: placeholder, value: '' }, ...rest]

const TestSelectDraftModal: FC<{ longreadId?: string }> = ({
  longreadId
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const clockClipBase = useMemo(
    () => `clip-clock-${Math.random().toString(36).slice(2, 11)}`,
    []
  )

  const [search, setSearch] = useState('')
  const [course, setCourse] = useState('')
  const [moduleRef, setModuleRef] = useState('')
  const [testDraft, setTestDraft] = useState('')

  const [copyFlags, setCopyFlags] = useState({
    title: true,
    author: true,
    questions: true,
    passScore: true,
    attempts: true,
    duration: true
  })

  const searchLower = search.trim().toLowerCase()

  const searchMatches = useMemo(() => {
    if (!searchLower) return []
    return getAllTestsFlat().filter((row) => {
      const hay = [
        row.courseTitle,
        row.moduleTitle,
        row.test.title,
        row.test.description
      ]
        .join(' ')
        .toLowerCase()
      return hay.includes(searchLower)
    })
  }, [searchLower])

  const testsInModule = useMemo(() => {
    if (!course || !moduleRef) return []
    const c = TEST_DRAFT_COURSE_CATALOG.find((x) => x.id === course)
    const m = c?.modules.find((x) => x.id === moduleRef)
    return m?.tests ?? []
  }, [course, moduleRef])

  useEffect(() => {
    if (!testDraft) return
    if (
      testsInModule.length === 0 ||
      !testsInModule.some((x) => x.id === testDraft)
    ) {
      setTestDraft('')
    }
  }, [testsInModule, testDraft])

  const courseOptions = useMemo(
    () =>
      placeholderFirst(
        t('modal.test_select_draft.course_placeholder'),
        TEST_DRAFT_COURSE_CATALOG.map((c) => ({ label: c.title, value: c.id }))
      ),
    [t]
  )

  const moduleOptions = useMemo(() => {
    if (!course) {
      return placeholderFirst(t('modal.test_select_draft.module_placeholder'), [])
    }
    const c = TEST_DRAFT_COURSE_CATALOG.find((x) => x.id === course)
    const mods = c?.modules ?? []
    return placeholderFirst(
      t('modal.test_select_draft.module_placeholder'),
      mods.map((m) => ({ label: m.title, value: m.id }))
    )
  }, [course, t])

  const testOptions = useMemo(() => {
    if (!course || !moduleRef) {
      return placeholderFirst(t('modal.test_select_draft.test_placeholder'), [])
    }
    return placeholderFirst(
      t('modal.test_select_draft.test_placeholder'),
      testsInModule.map((test) => ({ label: test.title, value: test.id }))
    )
  }, [course, moduleRef, testsInModule, t])

  const courseValue = useMemo(
    () => courseOptions.find((o) => o.value === course) ?? courseOptions[0],
    [course, courseOptions]
  )
  const moduleValue = useMemo(
    () => moduleOptions.find((o) => o.value === moduleRef) ?? moduleOptions[0],
    [moduleRef, moduleOptions]
  )
  const testValue = useMemo(
    () => testOptions.find((o) => o.value === testDraft) ?? testOptions[0],
    [testDraft, testOptions]
  )

  const onCourseChange = useCallback((item: any) => {
    const v = item?.value ?? ''
    setCourse(v)
    setModuleRef('')
    setTestDraft('')
  }, [])

  const onModuleChange = useCallback((item: any) => {
    const v = item?.value ?? ''
    setModuleRef(v)
    setTestDraft('')
  }, [])

  const selectedTest = useMemo(
    () =>
      course && moduleRef && testDraft
        ? findTestDraft(course, moduleRef, testDraft)
        : null,
    [course, moduleRef, testDraft]
  )

  const isRowSelected = useCallback(
    (row: FlatTestRow) =>
      course === row.courseId &&
      moduleRef === row.moduleId &&
      testDraft === row.test.id,
    [course, moduleRef, testDraft]
  )

  const onSelectCard = useCallback((row: FlatTestRow) => {
    setCourse(row.courseId)
    setModuleRef(row.moduleId)
    setTestDraft(row.test.id)
  }, [])

  const toggleCopy = useCallback((key: keyof typeof copyFlags) => {
    setCopyFlags((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const formatStatQuestions = useCallback(
    (n: number) =>
      t('modal.test_select_draft.stat_questions').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const formatStatPass = useCallback(
    (percent: number) =>
      t('modal.test_select_draft.stat_pass').replace(
        '{{percent}}',
        String(percent)
      ),
    [t]
  )

  const formatStatMinutes = useCallback(
    (n: number) =>
      t('modal.test_select_draft.stat_minutes').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const formatCopyQuestions = useCallback(
    (n: number) =>
      t('modal.test_select_draft.copy_questions').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const formatCopyPass = useCallback(
    (percent: number) =>
      t('modal.test_select_draft.copy_pass').replace(
        '{{percent}}',
        String(percent)
      ),
    [t]
  )

  const formatCopyAttempts = useCallback(
    (n: number) =>
      t('modal.test_select_draft.copy_attempts').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const formatCopyMinutes = useCallback(
    (n: number) =>
      t('modal.test_select_draft.copy_minutes').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const breadcrumbForRow = useCallback(
    (row: FlatTestRow) =>
      t('modal.test_select_draft.breadcrumb_line')
        .replace('{{course}}', row.courseTitle)
        .replace('{{module}}', row.moduleTitle)
        .replace('{{test}}', row.test.title),
    [t]
  )

  const handleCreateCopy = useCallback(() => {
    if (!selectedTest) return
    dispatch(
      modalControllerActions.showModal({
        name: 'testCreate',
        args: { createMode: 'existing', longreadId }
      })
    )
  }, [dispatch, longreadId, selectedTest])

  const showSearchGrid = searchMatches.length > 0

  return (
    <Root>
      <MainTitle>{t('modal.test_select_draft.title')}</MainTitle>
      <IntroText>{t('modal.test_select_draft.intro')}</IntroText>

      <SearchRow>
        <SearchIconWrap aria-hidden>
          <SearchIcon16 fill="#909090" />
        </SearchIconWrap>
        <SearchInput
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('modal.test_select_draft.search_placeholder')}
          autoComplete="off"
        />
      </SearchRow>

      {showSearchGrid && (
        <PreviewCardsGrid>
          {searchMatches.map((row) => (
            <TestPreviewCard
              key={`${row.courseId}-${row.moduleId}-${row.test.id}`}
              type="button"
              $selected={isRowSelected(row)}
              aria-pressed={isRowSelected(row)}
              onClick={() => onSelectCard(row)}
            >
              <PreviewBreadcrumbs>{breadcrumbForRow(row)}</PreviewBreadcrumbs>
              <PreviewCardTitle>{row.test.title}</PreviewCardTitle>
              <PreviewCardDescription>
                {row.test.description}
              </PreviewCardDescription>
              <PreviewStatsRow>
                <PreviewStatItem>
                  <QuestionStatIcon />
                  {formatStatQuestions(row.test.questionCount)}
                </PreviewStatItem>
                <PreviewStatItem>
                  <GradCapStatIcon />
                  {formatStatPass(row.test.passScorePercent)}
                </PreviewStatItem>
                <PreviewStatItem>
                  <ClockStatIcon
                    clipId={`${clockClipBase}-${row.courseId}-${row.moduleId}-${row.test.id}`}
                  />
                  {formatStatMinutes(row.test.durationMinutes)}
                </PreviewStatItem>
              </PreviewStatsRow>
            </TestPreviewCard>
          ))}
        </PreviewCardsGrid>
      )}

      <SectionTitle>{t('modal.test_select_draft.section_from')}</SectionTitle>

      <DropdownsGrid>
        <DropdownField>
          <CommonInput
            label={t('modal.test_select_draft.course_label')}
            orientation="column"
            type={INPUT_TYPES.dropdown}
            options={courseOptions as any}
            value={courseValue as any}
            onWhiteBG
            onChange={onCourseChange}
          />
        </DropdownField>

        <DropdownField>
          <CommonInput
            label={t('modal.test_select_draft.module_label')}
            orientation="column"
            type={INPUT_TYPES.dropdown}
            options={moduleOptions as any}
            value={moduleValue as any}
            onWhiteBG
            onChange={onModuleChange}
          />
        </DropdownField>

        <DropdownField>
          <CommonInput
            label={t('modal.test_select_draft.test_label')}
            orientation="column"
            type={INPUT_TYPES.dropdown}
            options={testOptions as any}
            value={testValue as any}
            onWhiteBG
            onChange={(item: any) => setTestDraft(item?.value ?? '')}
          />
        </DropdownField>
      </DropdownsGrid>

      {selectedTest && (
        <CopySection>
          <SectionTitle>
            {t('modal.test_select_draft.section_copy')}
          </SectionTitle>
          <CopyCheckboxStack>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.title}
                onChange={() => toggleCopy('title')}
              />
              <span>{t('modal.test_select_draft.copy_title')}</span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.author}
                onChange={() => toggleCopy('author')}
              />
              <span>{t('modal.test_select_draft.copy_author')}</span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.questions}
                onChange={() => toggleCopy('questions')}
              />
              <span>
                {formatCopyQuestions(selectedTest.questionCount)}
              </span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.passScore}
                onChange={() => toggleCopy('passScore')}
              />
              <span>
                {formatCopyPass(selectedTest.passScorePercent)}
              </span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.attempts}
                onChange={() => toggleCopy('attempts')}
              />
              <span>
                {formatCopyAttempts(selectedTest.attempts)}
              </span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.duration}
                onChange={() => toggleCopy('duration')}
              />
              <span>
                {formatCopyMinutes(selectedTest.durationMinutes)}
              </span>
            </CopyCheckboxRow>
          </CopyCheckboxStack>

          <CopyActionSlot>
            <BrgButton
              variant={ButtonVariant.Filled}
              fullWidth
              onClick={handleCreateCopy}
            >
              {t('modal.test_select_draft.create_copy')}
            </BrgButton>
          </CopyActionSlot>
        </CopySection>
      )}
    </Root>
  )
}

export default TestSelectDraftModal
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { BrgButton, ButtonVariant, CommonInput, INPUT_TYPES } from '@brg/ui'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { modalControllerActions } from 'core'
import SearchIcon16 from 'shared/icons/SearchIcon16'
import {
  TEST_DRAFT_COURSE_CATALOG,
  findTestDraft,
  getAllTestsFlat,
  type FlatTestRow
} from './mockTestDraftData'
import {
  ClockStatIcon,
  GradCapStatIcon,
  QuestionStatIcon
} from './TestPreviewIcons'
import {
  Root,
  MainTitle,
  IntroText,
  SearchRow,
  SearchIconWrap,
  SearchInput,
  SectionTitle,
  DropdownsGrid,
  DropdownField,
  PreviewCardsGrid,
  TestPreviewCard,
  PreviewBreadcrumbs,
  PreviewCardTitle,
  PreviewCardDescription,
  PreviewStatsRow,
  PreviewStatItem,
  CopySection,
  CopyCheckboxStack,
  CopyCheckboxRow,
  CopyActionSlot
} from './styles'

type DropdownOption = { label: string; value: string }

const placeholderFirst = (
  placeholder: string,
  rest: DropdownOption[]
): DropdownOption[] => [{ label: placeholder, value: '' }, ...rest]

const TestSelectDraftModal: FC<{ longreadId?: string }> = ({
  longreadId
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const clockClipBase = useMemo(
    () => `clip-clock-${Math.random().toString(36).slice(2, 11)}`,
    []
  )

  const [search, setSearch] = useState('')
  const [course, setCourse] = useState('')
  const [moduleRef, setModuleRef] = useState('')
  const [testDraft, setTestDraft] = useState('')

  const [copyFlags, setCopyFlags] = useState({
    title: true,
    author: true,
    questions: true,
    passScore: true,
    attempts: true,
    duration: true
  })

  const searchLower = search.trim().toLowerCase()

  const searchMatches = useMemo(() => {
    if (!searchLower) return []
    return getAllTestsFlat().filter((row) => {
      const hay = [
        row.courseTitle,
        row.moduleTitle,
        row.test.title,
        row.test.description
      ]
        .join(' ')
        .toLowerCase()
      return hay.includes(searchLower)
    })
  }, [searchLower])

  const testsInModule = useMemo(() => {
    if (!course || !moduleRef) return []
    const c = TEST_DRAFT_COURSE_CATALOG.find((x) => x.id === course)
    const m = c?.modules.find((x) => x.id === moduleRef)
    return m?.tests ?? []
  }, [course, moduleRef])

  useEffect(() => {
    if (!testDraft) return
    if (
      testsInModule.length === 0 ||
      !testsInModule.some((x) => x.id === testDraft)
    ) {
      setTestDraft('')
    }
  }, [testsInModule, testDraft])

  const courseOptions = useMemo(
    () =>
      placeholderFirst(
        t('modal.test_select_draft.course_placeholder'),
        TEST_DRAFT_COURSE_CATALOG.map((c) => ({ label: c.title, value: c.id }))
      ),
    [t]
  )

  const moduleOptions = useMemo(() => {
    if (!course) {
      return placeholderFirst(t('modal.test_select_draft.module_placeholder'), [])
    }
    const c = TEST_DRAFT_COURSE_CATALOG.find((x) => x.id === course)
    const mods = c?.modules ?? []
    return placeholderFirst(
      t('modal.test_select_draft.module_placeholder'),
      mods.map((m) => ({ label: m.title, value: m.id }))
    )
  }, [course, t])

  const testOptions = useMemo(() => {
    if (!course || !moduleRef) {
      return placeholderFirst(t('modal.test_select_draft.test_placeholder'), [])
    }
    return placeholderFirst(
      t('modal.test_select_draft.test_placeholder'),
      testsInModule.map((test) => ({ label: test.title, value: test.id }))
    )
  }, [course, moduleRef, testsInModule, t])

  const courseValue = useMemo(
    () => courseOptions.find((o) => o.value === course) ?? courseOptions[0],
    [course, courseOptions]
  )
  const moduleValue = useMemo(
    () => moduleOptions.find((o) => o.value === moduleRef) ?? moduleOptions[0],
    [moduleRef, moduleOptions]
  )
  const testValue = useMemo(
    () => testOptions.find((o) => o.value === testDraft) ?? testOptions[0],
    [testDraft, testOptions]
  )

  const onCourseChange = useCallback((item: any) => {
    const v = item?.value ?? ''
    setCourse(v)
    setModuleRef('')
    setTestDraft('')
  }, [])

  const onModuleChange = useCallback((item: any) => {
    const v = item?.value ?? ''
    setModuleRef(v)
    setTestDraft('')
  }, [])

  const selectedTest = useMemo(
    () =>
      course && moduleRef && testDraft
        ? findTestDraft(course, moduleRef, testDraft)
        : null,
    [course, moduleRef, testDraft]
  )

  const isRowSelected = useCallback(
    (row: FlatTestRow) =>
      course === row.courseId &&
      moduleRef === row.moduleId &&
      testDraft === row.test.id,
    [course, moduleRef, testDraft]
  )

  const onSelectCard = useCallback((row: FlatTestRow) => {
    setCourse(row.courseId)
    setModuleRef(row.moduleId)
    setTestDraft(row.test.id)
  }, [])

  const toggleCopy = useCallback((key: keyof typeof copyFlags) => {
    setCopyFlags((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const formatStatQuestions = useCallback(
    (n: number) =>
      t('modal.test_select_draft.stat_questions').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const formatStatPass = useCallback(
    (percent: number) =>
      t('modal.test_select_draft.stat_pass').replace(
        '{{percent}}',
        String(percent)
      ),
    [t]
  )

  const formatStatMinutes = useCallback(
    (n: number) =>
      t('modal.test_select_draft.stat_minutes').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const formatCopyQuestions = useCallback(
    (n: number) =>
      t('modal.test_select_draft.copy_questions').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const formatCopyPass = useCallback(
    (percent: number) =>
      t('modal.test_select_draft.copy_pass').replace(
        '{{percent}}',
        String(percent)
      ),
    [t]
  )

  const formatCopyAttempts = useCallback(
    (n: number) =>
      t('modal.test_select_draft.copy_attempts').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const formatCopyMinutes = useCallback(
    (n: number) =>
      t('modal.test_select_draft.copy_minutes').replace(
        '{{count}}',
        String(n)
      ),
    [t]
  )

  const breadcrumbForRow = useCallback(
    (row: FlatTestRow) =>
      t('modal.test_select_draft.breadcrumb_line')
        .replace('{{course}}', row.courseTitle)
        .replace('{{module}}', row.moduleTitle)
        .replace('{{test}}', row.test.title),
    [t]
  )

  const handleCreateCopy = useCallback(() => {
    if (!selectedTest) return
    dispatch(
      modalControllerActions.showModal({
        name: 'testCreate',
        args: { createMode: 'existing', longreadId }
      })
    )
  }, [dispatch, longreadId, selectedTest])

  const showSearchGrid = searchMatches.length > 0

  return (
    <Root>
      <MainTitle>{t('modal.test_select_draft.title')}</MainTitle>
      <IntroText>{t('modal.test_select_draft.intro')}</IntroText>

      <SearchRow>
        <SearchIconWrap aria-hidden>
          <SearchIcon16 fill="#909090" />
        </SearchIconWrap>
        <SearchInput
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('modal.test_select_draft.search_placeholder')}
          autoComplete="off"
        />
      </SearchRow>

      {showSearchGrid && (
        <PreviewCardsGrid>
          {searchMatches.map((row) => (
            <TestPreviewCard
              key={`${row.courseId}-${row.moduleId}-${row.test.id}`}
              type="button"
              $selected={isRowSelected(row)}
              aria-pressed={isRowSelected(row)}
              onClick={() => onSelectCard(row)}
            >
              <PreviewBreadcrumbs>{breadcrumbForRow(row)}</PreviewBreadcrumbs>
              <PreviewCardTitle>{row.test.title}</PreviewCardTitle>
              <PreviewCardDescription>
                {row.test.description}
              </PreviewCardDescription>
              <PreviewStatsRow>
                <PreviewStatItem>
                  <QuestionStatIcon />
                  {formatStatQuestions(row.test.questionCount)}
                </PreviewStatItem>
                <PreviewStatItem>
                  <GradCapStatIcon />
                  {formatStatPass(row.test.passScorePercent)}
                </PreviewStatItem>
                <PreviewStatItem>
                  <ClockStatIcon
                    clipId={`${clockClipBase}-${row.courseId}-${row.moduleId}-${row.test.id}`}
                  />
                  {formatStatMinutes(row.test.durationMinutes)}
                </PreviewStatItem>
              </PreviewStatsRow>
            </TestPreviewCard>
          ))}
        </PreviewCardsGrid>
      )}

      <SectionTitle>{t('modal.test_select_draft.section_from')}</SectionTitle>

      <DropdownsGrid>
        <DropdownField>
          <CommonInput
            label={t('modal.test_select_draft.course_label')}
            orientation="column"
            type={INPUT_TYPES.dropdown}
            options={courseOptions as any}
            value={courseValue as any}
            onWhiteBG
            onChange={onCourseChange}
          />
        </DropdownField>

        <DropdownField>
          <CommonInput
            label={t('modal.test_select_draft.module_label')}
            orientation="column"
            type={INPUT_TYPES.dropdown}
            options={moduleOptions as any}
            value={moduleValue as any}
            onWhiteBG
            onChange={onModuleChange}
          />
        </DropdownField>

        <DropdownField>
          <CommonInput
            label={t('modal.test_select_draft.test_label')}
            orientation="column"
            type={INPUT_TYPES.dropdown}
            options={testOptions as any}
            value={testValue as any}
            onWhiteBG
            onChange={(item: any) => setTestDraft(item?.value ?? '')}
          />
        </DropdownField>
      </DropdownsGrid>

      {selectedTest && (
        <CopySection>
          <SectionTitle>
            {t('modal.test_select_draft.section_copy')}
          </SectionTitle>
          <CopyCheckboxStack>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.title}
                onChange={() => toggleCopy('title')}
              />
              <span>{t('modal.test_select_draft.copy_title')}</span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.author}
                onChange={() => toggleCopy('author')}
              />
              <span>{t('modal.test_select_draft.copy_author')}</span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.questions}
                onChange={() => toggleCopy('questions')}
              />
              <span>
                {formatCopyQuestions(selectedTest.questionCount)}
              </span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.passScore}
                onChange={() => toggleCopy('passScore')}
              />
              <span>
                {formatCopyPass(selectedTest.passScorePercent)}
              </span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.attempts}
                onChange={() => toggleCopy('attempts')}
              />
              <span>
                {formatCopyAttempts(selectedTest.attempts)}
              </span>
            </CopyCheckboxRow>
            <CopyCheckboxRow>
              <input
                type="checkbox"
                checked={copyFlags.duration}
                onChange={() => toggleCopy('duration')}
              />
              <span>
                {formatCopyMinutes(selectedTest.durationMinutes)}
              </span>
            </CopyCheckboxRow>
          </CopyCheckboxStack>

          <CopyActionSlot>
            <BrgButton
              variant={ButtonVariant.Filled}
              fullWidth
              onClick={handleCreateCopy}
            >
              {t('modal.test_select_draft.create_copy')}
            </BrgButton>
          </CopyActionSlot>
        </CopySection>
      )}
    </Root>
  )
}

export default TestSelectDraftModal
